import axios from "axios";
import jwtDecode from "jwt-decode";
import dayjs from "dayjs";

export let apiToken = JSON.parse(localStorage.getItem("user_profile"));

// Function to handle logout
const handleLogout = () => {
  try {
    const tokenData = JSON.parse(localStorage.getItem("user_profile"));
    if (tokenData?.accessToken) {
      axios.post(
        `${process.env.REACT_APP_BACKEND_URL}Auth/logout`,
        { refreshToken: tokenData?.refreshToken },
        { headers: { Authorization: `Bearer ${tokenData.accessToken}` } }
      ).catch(() => {});
    }
  } catch (error) {
    // ignore - local session is cleared regardless
  }
  localStorage.removeItem("user_profile");
  localStorage.removeItem("user");
  window.location.replace("/");
};

// Attempt to exchange the stored refresh token for a new token pair
const refreshAccessToken = async () => {
  const tokenData = JSON.parse(localStorage.getItem("user_profile"));
  if (!tokenData?.refreshToken) return null;

  const response = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}Auth/refresh`,
    { refreshToken: tokenData.refreshToken }
  );
  const newTokens = response?.data?.data;
  if (!newTokens?.accessToken) return null;

  localStorage.setItem("user_profile", JSON.stringify(newTokens, null, 2));
  apiToken = newTokens;
  return newTokens;
};

// Common response interceptor for 401 errors
const setupResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response && error.response.status === 401) {
        const originalRequest = error.config;
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const newTokens = await refreshAccessToken();
            if (newTokens) {
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            // fall through to logout
          }
        }
        handleLogout();
      }
      return Promise.reject(error);
    }
  );
};

const createAxiosInstance = (baseURL, contentType = "application/json") => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": contentType,
      ...(apiToken?.accessToken && {
        Authorization: "Bearer " + apiToken.accessToken,
      }),
    },
  });

  // Add request interceptor for token refresh
  instance.interceptors.request.use(
    async (req) => {
      if (!apiToken) {
        apiToken = localStorage.getItem("user_profile")
          ? JSON.parse(localStorage.getItem("user_profile"))
          : null;
      }

      if (apiToken?.accessToken) {
        const user = jwtDecode(apiToken?.accessToken);
        const isExpired = dayjs.unix(user?.exp).diff(dayjs()) < 1;

        if (isExpired) {
          try {
            const newTokens = await refreshAccessToken();
            if (!newTokens) {
              handleLogout();
              return Promise.reject(new Error("Token expired"));
            }
            req.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          } catch (refreshError) {
            handleLogout();
            return Promise.reject(new Error("Token expired"));
          }
        } else {
          req.headers.Authorization = `Bearer ${apiToken.accessToken}`;
        }
      } else {
        delete req.headers.Authorization;
      }

      return req;
    },
    (error) => Promise.reject(error)
  );

  // Add response interceptor for 401 errors
  setupResponseInterceptor(instance);

  return instance;
};

// Create all API instances
export const api = createAxiosInstance(
  process.env.REACT_APP_ONBOARDING_BACKEND_URL
);
export const walletApi = createAxiosInstance(process.env.REACT_APP_WALLET_URL);
export const reportApi = createAxiosInstance(process.env.REACT_APP_REPORT_URL);
export const invoiceApi = createAxiosInstance(
  process.env.REACT_APP_INVOICE_URL
);
export const configApi = createAxiosInstance(process.env.REACT_APP_CONFIG_URL);
export const performanceApi = createAxiosInstance(
  process.env.REACT_APP_ANALYTICS_URL
);
export const flexDealOnboardingApi = createAxiosInstance(
  process.env.REACT_APP_FLEX_DEAL_ONBOARDING_URL
);

// Create form data instances
export const formApi = createAxiosInstance(
  process.env.REACT_APP_ONBOARDING_BACKEND_URL,
  "multipart/form-data"
);
export const formApiInvoice = createAxiosInstance(
  process.env.REACT_APP_INVOICE_URL,
  "multipart/form-data"
);

export default api;
